<script setup lang="ts">
import { isFreeCleanTemplate, PDF_TEMPLATES, type PdfTemplateId } from '~/constants/cvTemplates'
import type { TemplateShot } from './TemplateLightbox.vue'

/**
 * The PDF layout switch above a generated CV's preview. Whichever is picked is
 * the one that gets downloaded, bought and saved.
 *
 * Phones and tablets show the picked one by name, and the arrows either side
 * step through the rest, so the switch takes one slim row above the CV
 * itself. From `lg` up all eight fit as
 * page thumbnails, the same shots as the strip under the chat: a click picks
 * the layout, and the arrow in the corner opens the page large.
 *
 * Every layout but Basic is marked Premium: it downloads watermarked for
 * free, clean only paid, and the visitor should know before picking it.
 */
const model = defineModel<PdfTemplateId>({ required: true })

const { t } = useI18n()

const shots = computed<TemplateShot[]>(() => PDF_TEMPLATES.map(id => ({
  id,
  name: t(`cv.templates.${id}.name`),
  src: `/${id}.png`,
  width: 900,
  height: 1272,
})))

/** Phones: the arrows go round, from the last layout back to the first. */
function step(by: number) {
  const index = PDF_TEMPLATES.indexOf(model.value)
  model.value = PDF_TEMPLATES[(index + by + PDF_TEMPLATES.length) % PDF_TEMPLATES.length]!
}

const lightbox = ref<{ open: (shots: TemplateShot[], index: number) => void } | null>(null)
</script>

<template>
  <div>
    <!-- Phones and tablets: one name, the arrows step through the layouts -->
    <div class="mb-3 flex items-center gap-1 rounded-full border border-line bg-white p-1 lg:hidden dark:border-gray-700 dark:bg-gray-800">
      <button type="button" class="step-btn" :aria-label="t('cv.templates.previousTemplate')" @click="step(-1)">
        <i class="bi bi-chevron-left" aria-hidden="true" />
      </button>
      <p class="min-w-0 grow truncate text-center !text-sm font-semibold text-ink dark:text-white" aria-live="polite">
        <span class="sr-only">{{ t('cv.templates.title') }}: </span>{{ t(`cv.templates.${model}.name`) }}
        <span v-if="isFreeCleanTemplate(model)" class="ml-1.5 rounded-full bg-accent-strong px-1.5 py-0.5 align-middle !text-[0.625rem] font-bold !leading-tight !text-white">{{ t('cvChat.templates.free') }}</span>
        <i v-if="!isFreeCleanTemplate(model)" class="bi bi-gem ml-1 text-xs" aria-hidden="true" />
        <span v-if="!isFreeCleanTemplate(model)" class="sr-only">({{ t('cvChat.templates.premium') }})</span>
        <span class="ml-1.5 font-normal text-muted dark:text-gray-400">{{ PDF_TEMPLATES.indexOf(model) + 1 }}/{{ PDF_TEMPLATES.length }}</span>
      </p>
      <button type="button" class="step-btn" :aria-label="t('cv.templates.nextTemplate')" @click="step(1)">
        <i class="bi bi-chevron-right" aria-hidden="true" />
      </button>
    </div>

    <!-- Desktop: every layout at once, as a page -->
    <div role="radiogroup" :aria-label="t('cv.templates.selectTemplate')" class="mb-4 hidden grid-cols-8 gap-2 lg:grid">
      <div v-for="(shot, index) in shots" :key="shot.id" class="group relative">
        <button
          type="button"
          role="radio"
          :aria-checked="model === shot.id"
          class="block w-full focus:outline-none"
          @click="model = shot.id as PdfTemplateId"
        >
          <span class="thumb" :class="model === shot.id ? 'thumb-active' : ''">
            <NuxtImg
              :src="shot.src"
              :alt="t('cvChat.templates.alt', { name: shot.name })"
              format="webp"
              quality="80"
              width="224"
              height="317"
              sizes="lg:112px"
              loading="lazy"
              class="aspect-[1/1.414] w-full object-cover object-top"
            />
            <span
              v-if="isFreeCleanTemplate(shot.id)"
              class="absolute left-1 top-1 rounded-full bg-accent-strong px-1.5 py-0.5 !text-[0.625rem] font-bold !leading-tight !text-white"
            >
              {{ t('cvChat.templates.free') }}
            </span>
            <!-- A thumbnail is too narrow for the word next to the expand button: the gem, named for readers -->
            <span
              v-else
              class="absolute left-1 top-1 inline-flex size-5 items-center justify-center rounded-full bg-lilac !text-[0.625rem] !leading-none"
              :title="t('cvChat.templates.premium')"
            >
              <i class="bi bi-gem !text-ink" aria-hidden="true" />
              <span class="sr-only">{{ t('cvChat.templates.premium') }}</span>
            </span>
          </span>
          <span
            class="mt-1.5 block truncate text-center !text-xs"
            :class="model === shot.id ? 'font-semibold text-ink dark:text-white' : 'text-muted dark:text-gray-400'"
          >
            {{ shot.name }}
          </span>
        </button>

        <!-- Beside the radio, not inside it: opens the page large -->
        <button
          type="button"
          class="expand-btn"
          :aria-label="t('cvChat.templates.open', { name: shot.name })"
          @click="lightbox?.open(shots, index)"
        >
          <i class="bi bi-arrows-angle-expand" aria-hidden="true" />
        </button>
      </div>
    </div>

    <LandingTemplateLightbox ref="lightbox" />
  </div>
</template>

<style scoped>
.step-btn {
  @apply inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-ink text-white transition-opacity hover:opacity-80 dark:bg-white dark:text-ink;
}

.thumb {
  @apply relative block overflow-hidden rounded-md border border-line bg-white shadow-sm transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lift dark:border-gray-700;
}

button:focus-visible .thumb {
  @apply ring-2 ring-accent;
}

.thumb-active {
  @apply border-ink ring-2 ring-ink dark:border-white dark:ring-white;
}

.expand-btn {
  @apply absolute right-1 top-1 inline-flex size-6 items-center justify-center rounded-full bg-white text-xs text-ink shadow-lift transition duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent group-hover:-translate-y-0.5;
}
</style>
